/**
 * Fields API
 */

import { api, type ApiResponse, type ApiListResponse } from '@/lib/api';
import { getFieldDisplayName } from '@/utils/get-field-display-name';

// ============== Types ==============

export interface FieldSchema {
    name: string;
    table: string;
    schema: string;
    data_type: string;
    default_value: unknown;
    max_length: number | null;
    numeric_precision: number | null;
    numeric_scale: number | null;
    is_nullable: boolean;
    is_unique: boolean;
    is_indexed: boolean;
    is_primary_key: boolean;
    is_generated: boolean;
    has_auto_increment: boolean;
    foreign_key_column: string | null;
    foreign_key_table: string | null;
    comment?: string | null;
}

export interface FieldTranslation {
    language: string;
    translation: string;
}

export interface FieldMeta {
    id?: number;
    collection: string;
    field: string;
    special?: string[] | null;
    interface?: string | null;
    options?: Record<string, unknown> | null;
    display?: string | null;
    display_options?: Record<string, unknown> | null;
    readonly?: boolean;
    hidden?: boolean;
    sort?: number | null;
    width?: 'half' | 'half-left' | 'half-right' | 'full' | 'fill' | null;
    translations?: FieldTranslation[] | null;
    note?: string | null;
    conditions?: Record<string, unknown>[] | null;
    required?: boolean;
    group?: string | null;
    validation?: Record<string, unknown> | null;
    validation_message?: string | null;
    system?: boolean;
}

export interface Field {
    collection: string;
    field: string;
    type: string;
    name?: string;
    schema: FieldSchema | null;
    meta: FieldMeta | null;
}

export interface CreateFieldPayload {
    field: string;
    type: string;
    schema?: Partial<FieldSchema>;
    meta?: Partial<FieldMeta>;
}

export interface UpdateFieldPayload {
    type?: string;
    schema?: Partial<FieldSchema>;
    meta?: Partial<FieldMeta>;
}

// Field types available
export const FIELD_TYPES = [
    { value: 'string', label: 'String', icon: 'Type' },
    { value: 'text', label: 'Text', icon: 'AlignLeft' },
    { value: 'boolean', label: 'Boolean', icon: 'ToggleLeft' },
    { value: 'integer', label: 'Integer', icon: 'Hash' },
    { value: 'bigInteger', label: 'Big Integer', icon: 'Hash' },
    { value: 'float', label: 'Float', icon: 'Hash' },
    { value: 'decimal', label: 'Decimal', icon: 'Hash' },
    { value: 'timestamp', label: 'Timestamp', icon: 'Clock' },
    { value: 'dateTime', label: 'DateTime', icon: 'Calendar' },
    { value: 'date', label: 'Date', icon: 'Calendar' },
    { value: 'time', label: 'Time', icon: 'Clock' },
    { value: 'json', label: 'JSON', icon: 'Braces' },
    { value: 'uuid', label: 'UUID', icon: 'Key' },
    { value: 'binary', label: 'Binary', icon: 'Binary' },
    { value: 'hash', label: 'Hash', icon: 'Lock' },
    { value: 'csv', label: 'CSV', icon: 'List' },
] as const;

// ============== API Functions ==============

/**
 * Add computed name property to a field
 */
function withDisplayName(field: Field): Field {
    return {
        ...field,
        name: getFieldDisplayName(field),
    };
}

export async function getFields(collection?: string): Promise<Field[]> {
    const url = collection ? `/fields/${collection}` : '/fields';
    const response = await api.get<ApiListResponse<Field>>(url);
    return (response.data ?? []).map(withDisplayName);
}

export async function getFieldsForCollection(collection: string): Promise<Field[]> {
    const response = await api.get<ApiListResponse<Field>>(`/fields/${collection}`);
    return (response.data ?? []).map(withDisplayName);
}

export async function getField(collection: string, field: string): Promise<Field> {
    const response = await api.get<ApiResponse<Field>>(`/fields/${collection}/${field}`);
    return withDisplayName(response.data);
}

export async function createField(collection: string, payload: CreateFieldPayload): Promise<Field> {
    const response = await api.post<ApiResponse<Field>>(`/fields/${collection}`, payload);
    return withDisplayName(response.data);
}

export async function updateField(
    collection: string,
    field: string,
    payload: UpdateFieldPayload
): Promise<Field> {
    const response = await api.patch<ApiResponse<Field>>(`/fields/${collection}/${field}`, payload);
    return withDisplayName(response.data);
}

export async function deleteField(collection: string, field: string): Promise<void> {
    await api.delete(`/fields/${collection}/${field}`);
}
